import React, { useState, useEffect, useCallback } from 'react';
import { 
  Settings, Key, RefreshCw, CheckCircle2, XCircle, AlertCircle, 
  Clock, Package, ChevronDown, ChevronRight, Eye, EyeOff,
  Truck, ShoppingBag, User, Phone, MapPin, CreditCard, DollarSign,
  Zap, Terminal, Play, Search, FileText, ClipboardCheck, Info
} from 'lucide-react';
import { MainLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import apiClient from '../lib/api';
import IFoodHomologationTab from "../components/ifood/IFoodHomologationTab";
import IFoodCatalogV2Tab from "../components/ifood/IFoodCatalogV2Tab";

const IFoodIntegrationPage = () => {
  const [storeId, setStoreId] = useState(null);
  const [stores, setStores] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [polling, setPolling] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [activeTab, setActiveTab] = useState('config');
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Catalog/Menu Sync states
  const [syncingCatalog, setSyncingCatalog] = useState(false);
  const [catalogSyncResult, setCatalogSyncResult] = useState(null);

  // Estados para modal de cancelamento
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReasons, setCancelReasons] = useState([]);
  const [selectedCancelReason, setSelectedCancelReason] = useState(null);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [loadingReasons, setLoadingReasons] = useState(false);

  // API Test states
  const [testingAuth, setTestingAuth] = useState(false);
  const [testingPolling, setTestingPolling] = useState(false);
  const [testingOrder, setTestingOrder] = useState(false);
  const [authTestResult, setAuthTestResult] = useState(null);
  const [pollingTestResult, setPollingTestResult] = useState(null);
  const [orderTestResult, setOrderTestResult] = useState(null);
  const [orderIdToTest, setOrderIdToTest] = useState('');


  // Form state
  const [formData, setFormData] = useState({
    merchant_id: '',
    client_id: '',
    client_secret: '',
    auto_confirm_orders: false,
    auto_print_orders: false,
    polling_enabled: true,
    polling_interval_seconds: 30
  });

  

  // Load stores on mount - smart default: prefer store with iFood orders
  useEffect(() => {
    const loadStores = async () => {
      try {
        const res = await apiClient.get('/rbac/stores');
        setStores(res.data || []);
        if (res.data?.length > 0) {
          // Try to pick the store that has iFood orders
          try {
            const statsRes = await apiClient.get('/ifood/stats/stores');
            const stats = statsRes.data || {};
            const storeWithOrders = res.data.find(s => stats[s.id] > 0);
            setStoreId(storeWithOrders?.id || res.data[0].id);
          } catch {
            setStoreId(res.data[0].id);
          }
        }
      } catch (err) {
        toast.error('Erro ao carregar lojas');
      }
    };
    loadStores();
  }, []);

  // Load config when store changes
  useEffect(() => {
    if (storeId) {
      loadConfig();
    }
  }, [storeId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/ifood/config/${storeId}`);
      setConfig(res.data);
      if (res.data.configured) {
        setFormData({
          merchant_id: res.data.merchant_id || '',
          client_id: res.data.client_id || '',
          client_secret: '',
          auto_confirm_orders: res.data.auto_confirm_orders || false,
          auto_print_orders: res.data.auto_print_orders || false,
          polling_enabled: res.data.polling_enabled ?? true,
          polling_interval_seconds: res.data.polling_interval_seconds || 30
        });
      }
    } catch (err) {
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const res = await apiClient.get(`/ifood/orders/${storeId}`);
      setOrders(res.data || []);
    } catch (err) {
      console.error('Error loading orders:', err);
    }
  };

  const loadEvents = async () => {
    try {
      const res = await apiClient.get(`/ifood/events/${storeId}?limit=50`);
      setEvents(res.data || []);
    } catch (err) {
      console.error('Error loading events:', err);
    }
  };

  useEffect(() => {
    if (storeId && activeTab === 'orders') {
      loadOrders();
    } else if (storeId && activeTab === 'events') {
      loadEvents();
    }
  }, [storeId, activeTab]);

  const handleSaveConfig = async () => {
    if (!formData.merchant_id || !formData.client_id) {
      toast.error('Merchant ID e Client ID são obrigatórios');
      return;
    }

    if (!config?.configured && !formData.client_secret) {
      toast.error('Client Secret é obrigatório para nova configuração');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        merchant_id: formData.merchant_id,
        client_id: formData.client_id,
        client_secret: formData.client_secret || undefined,
        auto_confirm_orders: formData.auto_confirm_orders,
        auto_print_orders: formData.auto_print_orders,
        polling_enabled: formData.polling_enabled,
        polling_interval_seconds: formData.polling_interval_seconds
      };

      if (config?.configured) {
        await apiClient.patch(`/ifood/config/${storeId}`, payload);
      } else {
        await apiClient.post(`/ifood/config/${storeId}`, payload);
      }
      
      toast.success('Configuração salva com sucesso');
      loadConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleAuthenticate = async () => {
    setAuthenticating(true);
    try {
      await apiClient.post(`/ifood/config/${storeId}/authenticate`);
      toast.success('Autenticação realizada com sucesso!');
      loadConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha na autenticação');
    } finally {
      setAuthenticating(false);
    }
  };

  const handlePollEvents = async () => {
    setPolling(true);
    try {
      const res = await apiClient.post(`/ifood/events/${storeId}/poll`);
      toast.success(res.data.message || 'Eventos verificados');
      loadOrders();
      loadEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao buscar eventos');
    } finally {
      setPolling(false);
    }
  };

  const handleConfirmOrder = async (orderId) => {
    try {
      await apiClient.post(`/ifood/orders/${storeId}/${orderId}/confirm`);
      toast.success('Pedido confirmado!');
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao confirmar pedido');
    }
  };

  const handleStartPreparation = async (orderId) => {
    try {
      await apiClient.post(`/ifood/orders/${storeId}/${orderId}/start-preparation`);
      toast.success('Preparo iniciado!');
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  // Handler inteligente: DELIVERY vai direto para dispatch, TAKEOUT usa readyToPickup
  const handleMarkReady = async (orderId, orderType) => {
    try {
      if (orderType === 'DELIVERY') {
        // DELIVERY: vai direto para dispatch (entrega iFood ou loja)
        await apiClient.post(`/ifood/orders/${storeId}/${orderId}/dispatch`);
        toast.success('Pedido despachado para entrega!');
      } else {
        // TAKEOUT: marca como pronto para retirada
        await apiClient.post(`/ifood/orders/${storeId}/${orderId}/ready-to-pickup`);
        toast.success('Pedido pronto para retirada!');
      }
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao marcar pedido');
    }
  };

  const handleReadyToPickup = async (orderId) => {
    try {
      await apiClient.post(`/ifood/orders/${storeId}/${orderId}/ready-to-pickup`);
      toast.success('Pedido pronto para retirada!');
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  const handleDispatch = async (orderId) => {
    try {
      await apiClient.post(`/ifood/orders/${storeId}/${orderId}/dispatch`);
      toast.success('Pedido despachado!');
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };


  // Estado para controle de cancelabilidade
  const [cancellationMessage, setCancellationMessage] = React.useState('');
  const [canCancelOrder, setCanCancelOrder] = React.useState(true);

  // Buscar motivos de cancelamento disponíveis
  const fetchCancellationReasons = async (order) => {
    setLoadingReasons(true);
    setOrderToCancel(order);
    setCancellationMessage('');
    setCanCancelOrder(true);
    
    try {
      const res = await apiClient.get(`/ifood/orders/${storeId}/${order.ifood_order_id}/cancellation-reasons`);
      const data = res.data;
      
      // TRATAMENTO DOS 3 CENÁRIOS
      if (data.cancellable === true) {
        // Cenário 1: Pedido PODE ser cancelado
        setCancelReasons(data.reasons || []);
        setCanCancelOrder(true);
        setCancellationMessage('');
      } else {
        // Cenário 2 e 3: Pedido NÃO pode ser cancelado ou erro
        setCancelReasons([]);
        setCanCancelOrder(false);
        setCancellationMessage(data.message || 'Este pedido não pode ser cancelado no momento');
      }
      setShowCancelModal(true);
    } catch (err) {
      // Erro técnico
      setCancelReasons([]);
      setCanCancelOrder(false);
      setCancellationMessage('Erro ao carregar motivos de cancelamento');
      setShowCancelModal(true);
    } finally {
      setLoadingReasons(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedCancelReason || !orderToCancel) {
      toast.error("Selecione um motivo de cancelamento");
      return;
    }
    try {
      await apiClient.post(`/ifood/orders/${storeId}/${orderToCancel.ifood_order_id}/cancel?reason=${selectedCancelReason.cancelCodeId}`);
      toast.success("Pedido cancelado!");
      setShowCancelModal(false);
      setSelectedCancelReason(null);
      setOrderToCancel(null);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cancelar pedido");
    }
  };

  const handleMarkDelivered = async (orderId) => {
    try {
      await apiClient.post(`/ifood/orders/${storeId}/${orderId}/delivered`);
      toast.success('Pedido marcado como entregue!');
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  // Mock mode handlers - for generating test orders
  const handleGenerateMockOrders = async () => {
    if (!config?.configured || config.auth_status !== 'AUTHORIZED') {
      toast.error('Configure e autentique primeiro');
      return;
    }
    try {
      const res = await apiClient.post(`/ifood/mock/${storeId}/generate-orders?count=3`);
      toast.success(res.data.message || 'Pedidos de teste gerados!');
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar pedidos');
    }
  };

  const handleClearMockData = async () => {
    try {
      const res = await apiClient.delete(`/ifood/mock/${storeId}/clear`);
      toast.success(res.data.message || 'Dados de teste removidos!');
      loadOrders();
      loadEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao limpar dados');
    }
  };


  // Catalog Sync handler - ISOLADO do 99Food
  const handleSyncCatalog = async () => {
    if (!config?.configured || config.auth_status !== 'AUTHORIZED') {
      toast.error('Configure e autentique primeiro');
      return;
    }
    setSyncingCatalog(true);
    setCatalogSyncResult(null);
    try {
      const res = await apiClient.post(`/ifood/catalog/${storeId}/sync`);
      setCatalogSyncResult(res.data);
      if (res.data.success) {
        toast.success(`Cardapio sincronizado! ${res.data.items_updated} atualizados itens enviados.`);
      } else {
        toast.error(res.data.message || 'Falha na sincronizacao');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      setCatalogSyncResult({ success: false, message: errorMsg, errors: [errorMsg] });
      toast.error(errorMsg);
    } finally {
      setSyncingCatalog(false);
    }
  };

  // API Test functions - Direct API testing
  const handleTestAuth = async () => {
    setTestingAuth(true);
    setAuthTestResult(null);
    try {
      const res = await apiClient.post('/ifood/test-auth');
      setAuthTestResult(res.data);
      if (res.data.success) {
        toast.success('Autenticação com iFood funcionando!');
      } else {
        toast.error('Falha na autenticação');
      }
    } catch (err) {
      setAuthTestResult({ success: false, error: err.response?.data?.detail || err.message });
      toast.error('Erro no teste de autenticação');
    } finally {
      setTestingAuth(false);
    }
  };

  const handleTestPolling = async () => {
    setTestingPolling(true);
    setPollingTestResult(null);
    try {
      const res = await apiClient.post('/ifood/test-polling');
      setPollingTestResult(res.data);
      if (res.data.success) {
        toast.success(`Polling funcionando! ${res.data.events?.length || 0} eventos encontrados`);
      } else {
        toast.error('Falha no polling');
      }
    } catch (err) {
      setPollingTestResult({ success: false, error: err.response?.data?.detail || err.message });
      toast.error('Erro no teste de polling');
    } finally {
      setTestingPolling(false);
    }
  };

  const handleTestOrder = async () => {
    if (!orderIdToTest.trim()) {
      toast.error('Digite o ID do pedido iFood');
      return;
    }
    setTestingOrder(true);
    setOrderTestResult(null);
    try {
      const res = await apiClient.get(`/ifood/test-order/${orderIdToTest.trim()}`);
      setOrderTestResult(res.data);
      if (res.data.success) {
        toast.success('Pedido encontrado!');
      } else {
        toast.error('Pedido não encontrado');
      }
    } catch (err) {
      setOrderTestResult({ success: false, error: err.response?.data?.detail || err.message });
      toast.error('Erro ao buscar pedido');
    } finally {
      setTestingOrder(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'PLACED': 'bg-yellow-100 text-yellow-800',
      'CONFIRMED': 'bg-blue-100 text-blue-800',
      'PREPARATION_STARTED': 'bg-purple-100 text-purple-800',
      'READY_TO_PICKUP': 'bg-green-100 text-green-800',
      'DISPATCHED': 'bg-indigo-100 text-indigo-800',
      'DELIVERED': 'bg-emerald-100 text-emerald-800',
      'CONCLUDED': 'bg-emerald-100 text-emerald-800',
      'CANCELLED': 'bg-red-100 text-red-800',
      'CANCELLATION_REQUESTED': 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'PLACED': 'Novo',
      'CONFIRMED': 'Confirmado',
      'PREPARATION_STARTED': 'Em Preparo',
      'READY_TO_PICKUP': 'Pronto',
      'DISPATCHED': 'Despachado',
      'DELIVERED': 'Entregue',
      'CONCLUDED': 'Concluído',
      'CANCELLED': 'Cancelado',
      'CANCELLATION_REQUESTED': 'Cancelamento Solicitado'
    };
    return labels[status] || status;
  };

  const AuthStatusBadge = () => {
    if (!config?.configured) {
      return (
        <span className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm">
          <AlertCircle className="w-4 h-4" />
          Não Configurado
        </span>
      );
    }

    if (config.auth_status === 'AUTHORIZED') {
      return (
        <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm">
          <CheckCircle2 className="w-4 h-4" />
          Autenticado
        </span>
      );
    }

    if (config.auth_status === 'EXPIRED') {
      return (
        <span className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm">
          <XCircle className="w-4 h-4" />
          Expirado
        </span>
      );
    }

    return (
      <span className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-full text-sm">
        <AlertCircle className="w-4 h-4" />
        Pendente
      </span>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
    <div className="p-6 space-y-6" data-testid="ifood-integration-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integração iFood</h1>
          <p className="text-gray-500 mt-1">Configure e gerencie a integração com o iFood</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Store selector */}
          <select
            value={storeId || ''}
            onChange={(e) => setStoreId(e.target.value)}
            className="px-4 py-2 border rounded-lg bg-white text-gray-700"
            data-testid="store-selector"
          >
            {stores.map(store => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </select>
          <AuthStatusBadge />
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('config')}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 transition-all ${
              activeTab === 'config' 
                ? 'border-red-500 text-red-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-config"
          >
            <Settings className="w-4 h-4" />
            Configuração
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 transition-all ${
              activeTab === 'orders' 
                ? 'border-red-500 text-red-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-orders"
          >
            <Package className="w-4 h-4" />
            Pedidos
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 transition-all ${
              activeTab === 'events' 
                ? 'border-red-500 text-red-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-events"
          >
            <Clock className="w-4 h-4" />
            Eventos
          </button>
          <button
            onClick={() => setActiveTab('api-tests')}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 transition-all ${
              activeTab === 'api-tests' 
                ? 'border-red-500 text-red-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-api-tests"
          >
            <Terminal className="w-4 h-4" />
            Testes API
          </button>
          <button
            onClick={() => setActiveTab('homologation')}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 transition-all ${
              activeTab === 'homologation' 
                ? 'border-red-500 text-red-600 bg-red-50' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-homologation"
          >
            <ClipboardCheck className="w-4 h-4" />
            Homologacao
          </button>
          <button
            onClick={() => setActiveTab("catalog-v2")}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === "catalog-v2" ? "border-red-500 text-red-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}
            data-testid="tab-catalog-v2"
          >
            <Package className="w-4 h-4" />
            Catalogo V2
          </button>
        </div>

        <div className="p-6">
          {/* Configuration Tab */}
          {activeTab === 'config' && (
            <div className="space-y-6">
              {/* Credentials Section */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Key className="w-5 h-5 text-red-500" />
                  Credenciais da API
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Merchant ID *
                    </label>
                    <Input
                      value={formData.merchant_id}
                      onChange={(e) => setFormData({...formData, merchant_id: e.target.value})}
                      placeholder="ID do merchant no iFood"
                      data-testid="input-merchant-id"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Encontre no Portal do Parceiro iFood
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client ID *
                    </label>
                    <Input
                      value={formData.client_id}
                      onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                      placeholder="Client ID da aplicação"
                      data-testid="input-client-id"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Secret {!config?.configured && '*'}
                    </label>
                    <div className="relative">
                      <Input
                        type={showSecret ? 'text' : 'password'}
                        value={formData.client_secret}
                        onChange={(e) => setFormData({...formData, client_secret: e.target.value})}
                        placeholder={config?.configured ? '••••••••• (deixe em branco para manter)' : 'Client Secret da aplicação'}
                        className="pr-10"
                        data-testid="input-client-secret"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecret(!showSecret)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Settings Section */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Configurações
                </h3>
                
                <div className="space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.polling_enabled}
                      onChange={(e) => setFormData({...formData, polling_enabled: e.target.checked})}
                      className="w-5 h-5 rounded border-gray-300 text-red-500 focus:ring-red-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Polling automático de eventos</span>
                      <p className="text-sm text-gray-500">Buscar novos pedidos automaticamente</p>
                    </div>
                  </label>

                  {formData.polling_enabled && (
                    <div className="ml-8">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Intervalo de polling (segundos)
                      </label>
                      <Input
                        type="number"
                        min={10}
                        max={300}
                        value={formData.polling_interval_seconds}
                        onChange={(e) => setFormData({...formData, polling_interval_seconds: parseInt(e.target.value)})}
                        className="w-32"
                      />
                    </div>
                  )}

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.auto_confirm_orders}
                      onChange={(e) => setFormData({...formData, auto_confirm_orders: e.target.checked})}
                      className="w-5 h-5 rounded border-gray-300 text-red-500 focus:ring-red-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Confirmar pedidos automaticamente</span>
                      <p className="text-sm text-gray-500">Pedidos serão confirmados assim que recebidos</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.auto_print_orders}
                      onChange={(e) => setFormData({...formData, auto_print_orders: e.target.checked})}
                      className="w-5 h-5 rounded border-gray-300 text-red-500 focus:ring-red-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Imprimir pedidos automaticamente</span>
                      <p className="text-sm text-gray-500">Enviar para impressão ao receber</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex gap-3">
                  <Button
                    onClick={handleSaveConfig}
                    disabled={saving}
                    className="bg-red-500 hover:bg-red-600 text-white"
                    data-testid="btn-save-config"
                  >
                    {saving ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Salvar Configuração
                  </Button>
                  
                  {config?.configured && (
                    <Button
                      onClick={handleAuthenticate}
                      disabled={authenticating}
                      variant="outline"
                      data-testid="btn-authenticate"
                    >
                      {authenticating ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Key className="w-4 h-4 mr-2" />
                      )}
                      Autenticar
                    </Button>
                  )}
                </div>

                <div className="flex gap-3">
                  {config?.configured && config.auth_status === 'AUTHORIZED' && (
                    <Button
                      onClick={handlePollEvents}
                      disabled={polling}
                      variant="outline"
                      className="border-green-500 text-green-600 hover:bg-green-50"
                      data-testid="btn-poll-events"
                    >
                      {polling ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Buscar Novos Pedidos
                    </Button>
                  )}
                </div>
              </div>

              {/* Test Authentication Section */}
              {config?.configured && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <h4 className="font-medium text-green-900 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Testar Conexão com iFood
                  </h4>
                  <p className="text-sm text-green-700 mb-3">
                    Use as credenciais configuradas para testar a autenticação com a API real do iFood.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleAuthenticate}
                      disabled={authenticating}
                      variant="outline"
                      size="sm"
                      className="border-green-500 text-green-700 hover:bg-green-100"
                      data-testid="btn-test-auth"
                    >
                      {authenticating ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Key className="w-4 h-4 mr-2" />
                      )}
                      Testar Autenticação
                    </Button>
                    {config.auth_status === 'AUTHORIZED' && (
                      <>
                        <Button
                          onClick={handleGenerateMockOrders}
                          variant="outline"
                          size="sm"
                          className="border-blue-500 text-blue-700 hover:bg-blue-100"
                          data-testid="btn-generate-mock"
                        >
                          <Package className="w-4 h-4 mr-2" />
                          Gerar Pedidos Teste
                        </Button>
                        <Button
                          onClick={handleClearMockData}
                          variant="outline"
                          size="sm"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          data-testid="btn-clear-mock"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Limpar Dados Teste
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}


              {/* Catalog Sync Section - ISOLADO do 99Food */}
              {config?.configured && config.auth_status === 'AUTHORIZED' && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <Package className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Sincronizar Cardapio</h3>
                        <p className="text-sm text-gray-500">Envia categorias e produtos para o iFood</p>
                      </div>
                    </div>
                    <Button
                      onClick={handleSyncCatalog}
                      disabled={syncingCatalog}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="btn-sync-catalog-ifood"
                    >
                      {syncingCatalog ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      {syncingCatalog ? 'Sincronizando...' : 'Sincronizar Cardapio'}
                    </Button>
                  </div>
                  
                  {catalogSyncResult && (
                    <div className={`mt-4 p-4 rounded-lg ${catalogSyncResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {catalogSyncResult.success ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className={`font-medium ${catalogSyncResult.success ? 'text-green-700' : 'text-red-700'}`}>
                          {catalogSyncResult.message}
                        </span>
                      </div>
                      <div className="text-sm mt-2 space-y-3">
                        <div className="flex items-center gap-4">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">{catalogSyncResult.correlation_id}</code>
                          {catalogSyncResult.sync_mode && (
                            <span className={`text-xs px-2 py-1 rounded font-semibold ${catalogSyncResult.sync_mode === "CATALOG_API" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                              {catalogSyncResult.sync_mode === "CATALOG_API" ? "UPSERT (Criar/Atualizar)" : "UPDATE_ONLY"}
                            </span>
                          )}
                          <span className="text-gray-500">|</span>
                          <span className="text-gray-600">{catalogSyncResult.duration_seconds}s</span>
                        </div>
                        {catalogSyncResult.mode_reason && (
                          <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded border">
                            <strong>Modo detectado:</strong> {catalogSyncResult.mode_reason}
                          </div>
                        )}
                        {catalogSyncResult.totals && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 p-2 bg-white rounded border">
                            <div className="text-center">
                              <div className="text-lg font-bold text-green-600">{catalogSyncResult.totals.created}</div>
                              <div className="text-xs text-gray-500">Criados</div>
                            </div>
                            <div className="text-center">
                              <div className="text-lg font-bold text-blue-600">{catalogSyncResult.totals.updated}</div>
                              <div className="text-xs text-gray-500">Atualizados</div>
                            </div>
                            <div className="text-center">
                              <div className="text-lg font-bold text-gray-400">{catalogSyncResult.totals.skipped}</div>
                              <div className="text-xs text-gray-500">Sem mudança</div>
                            </div>
                            <div className="text-center">
                              <div className="text-lg font-bold text-red-600">{catalogSyncResult.totals.failed}</div>
                              <div className="text-xs text-gray-500">Falhas</div>
                            </div>
                            {catalogSyncResult.totals.not_found > 0 && (
                              <div className="text-center">
                                <div className="text-lg font-bold text-yellow-600">{catalogSyncResult.totals.not_found}</div>
                                <div className="text-xs text-gray-500">No Portal</div>
                              </div>
                            )}
                          </div>
                        )}
                        {catalogSyncResult.categories && (
                          <details className="text-xs">
                            <summary className="cursor-pointer text-gray-600">Detalhes por entidade</summary>
                            <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1 text-center">
                              <div className="bg-gray-50 p-1 rounded">
                                <div className="font-medium">Categorias</div>
                                <div>C:{catalogSyncResult.categories.created} U:{catalogSyncResult.categories.updated} S:{catalogSyncResult.categories.skipped}</div>
                              </div>
                              <div className="bg-gray-50 p-1 rounded">
                                <div className="font-medium">Produtos</div>
                                <div>C:{catalogSyncResult.products?.created || 0} U:{catalogSyncResult.products?.updated || 0} S:{catalogSyncResult.products?.skipped || 0}</div>
                              </div>
                              <div className="bg-gray-50 p-1 rounded">
                                <div className="font-medium">Grupos</div>
                                <div>C:{catalogSyncResult.option_groups?.created || 0} U:{catalogSyncResult.option_groups?.updated || 0} S:{catalogSyncResult.option_groups?.skipped || 0}</div>
                              </div>
                              <div className="bg-gray-50 p-1 rounded">
                                <div className="font-medium">Opções</div>
                                <div>C:{catalogSyncResult.options?.created || 0} U:{catalogSyncResult.options?.updated || 0} S:{catalogSyncResult.options?.skipped || 0}</div>
                              </div>
                            </div>
                          </details>
                        )}
                        {catalogSyncResult.not_found_items?.length > 0 && (
                          <details className="mt-2 text-xs">
                            <summary className="cursor-pointer text-yellow-700">
                              {catalogSyncResult.not_found_items.length} itens precisam ser criados no Portal iFood
                            </summary>
                            <ul className="mt-1 max-h-32 overflow-y-auto bg-yellow-50 rounded p-2 text-yellow-800">
                              {catalogSyncResult.not_found_items.map((item, i) => (
                                <li key={i} className="truncate">{item}</li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                      {catalogSyncResult.errors?.length > 0 && (
                        <div className="mt-2 text-sm text-red-600">
                          <strong>Erros:</strong> {catalogSyncResult.errors.join(', ')}
                        </div>
                      )}
                      {catalogSyncResult.warnings?.length > 0 && (
                        <details className="mt-2">
                          <summary className="text-sm text-yellow-700 cursor-pointer">Avisos ({catalogSyncResult.warnings.length})</summary>
                          <ul className="text-xs text-yellow-600 mt-1">
                            {catalogSyncResult.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
                            {catalogSyncResult.warnings.length > 5 && <li>... e mais {catalogSyncResult.warnings.length - 5}</li>}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Status info */}
              {config?.configured && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Informações</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-blue-600">Status:</span>
                      <span className="ml-2 text-blue-900">{config.auth_status}</span>
                    </div>
                    <div>
                      <span className="text-blue-600">Última autenticação:</span>
                      <span className="ml-2 text-blue-900">
                        {config.last_auth_at ? new Date(config.last_auth_at).toLocaleString('pt-BR') : 'Nunca'}
                      </span>
                    </div>
                    <div>
                      <span className="text-blue-600">Token expira:</span>
                      <span className="ml-2 text-blue-900">
                        {config.token_expires_at ? new Date(config.token_expires_at).toLocaleString('pt-BR') : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-blue-600">Último poll:</span>
                      <span className="ml-2 text-blue-900">
                        {config.last_poll_at ? new Date(config.last_poll_at).toLocaleString('pt-BR') : 'Nunca'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Pedidos iFood</h3>
                <Button
                  onClick={() => { loadOrders(); handlePollEvents(); }}
                  variant="outline"
                  size="sm"
                  disabled={polling || !config?.configured || config.auth_status !== 'AUTHORIZED'}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${polling ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>

              {!config?.configured || config.auth_status !== 'AUTHORIZED' ? (
                <div className="text-center py-12 text-gray-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                  <p>Configure e autentique a integração para ver os pedidos</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Nenhum pedido encontrado</p>
                  <p className="text-sm mt-2">Clique em "Buscar Novos Pedidos" para verificar</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map(order => (
                    <div 
                      key={order.id} 
                      className="bg-white border rounded-xl p-4 hover:shadow-md transition-shadow"
                      data-testid={`order-${order.ifood_order_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-mono text-lg font-bold text-gray-900">
                              #{order.ifood_display_id || order.ifood_order_id.slice(0, 8)}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.ifood_status)}`}>
                              {getStatusLabel(order.ifood_status)}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              order.order_type === 'DELIVERY' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                            }`}>
                              {order.order_type === 'DELIVERY' ? (
                                <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> Delivery</span>
                              ) : (
                                <span className="flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> Retirada</span>
                              )}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            {order.customer_name && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <User className="w-4 h-4" />
                                {order.customer_name}
                              </div>
                            )}
                            {order.customer_phone && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Phone className="w-4 h-4" />
                                {order.customer_phone}
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-gray-600">
                              <CreditCard className="w-4 h-4" />
                              {order.payment_method || 'Online'}
                              {order.payment_prepaid && <span className="text-green-600 text-xs">(Pago)</span>}
                            </div>
                            <div className="flex items-center gap-2 font-semibold text-green-600">
                              <DollarSign className="w-4 h-4" />
                              R$ {order.total?.toFixed(2)}
                            </div>
                          </div>

                          {order.delivery_address && (
                            <div className="mt-2 flex items-start gap-2 text-sm text-gray-500">
                              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              <span>
                                {order.delivery_address.streetName}, {order.delivery_address.streetNumber}
                                {order.delivery_address.neighborhood && ` - ${order.delivery_address.neighborhood}`}
                              </span>
                            </div>
                          )}

                          {order.items && order.items.length > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-sm font-medium text-gray-700 mb-1">Itens:</p>
                              <div className="space-y-1">
                                {order.items.slice(0, 3).map((item, idx) => (
                                  <div key={idx} className="text-sm text-gray-600">
                                    {item.quantity}x {item.name} - R$ {item.totalPrice?.toFixed(2)}
                                  </div>
                                ))}
                                {order.items.length > 3 && (
                                  <div className="text-sm text-gray-400">
                                    + {order.items.length - 3} itens...
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Campos Extras para iFood */}
                          <div className="mt-3 pt-3 border-t space-y-2">
                            {/* Agendamento */}
                            {order.scheduled_datetime && (
                              <div className="flex items-center gap-2 text-sm bg-yellow-50 p-2 rounded">
                                <Clock className="w-4 h-4 text-yellow-600" />
                                <span className="font-medium text-yellow-700">Agendado para: {new Date(order.scheduled_datetime).toLocaleString("pt-BR")}</span>
                              </div>
                            )}
                            
                            {/* Troco */}
                            {order.change_for && order.change_for > 0 && (
                              <div className="flex items-center gap-2 text-sm bg-blue-50 p-2 rounded">
                                <DollarSign className="w-4 h-4 text-blue-600" />
                                <span>Troco para: <strong>R$ {order.change_for.toFixed(2)}</strong> (Troco: R$ {(order.change_for - order.total).toFixed(2)})</span>
                              </div>
                            )}
                            
                            {/* Bandeira do cartão */}
                            {order.card_brand && (
                              <div className="flex items-center gap-2 text-sm">
                                <CreditCard className="w-4 h-4 text-gray-500" />
                                <span>Bandeira: <strong>{order.card_brand}</strong></span>
                              </div>
                            )}
                            
                            {/* Código de coleta */}
                            {order.pickup_code && (
                              <div className="flex items-center gap-2 text-sm bg-green-50 p-2 rounded">
                                <Package className="w-4 h-4 text-green-600" />
                                <span>Código de coleta: <strong className="font-mono text-lg">{order.pickup_code}</strong></span>
                              </div>
                            )}
                            
                            {/* CPF/CNPJ */}
                            {order.customer_document && (
                              <div className="flex items-center gap-2 text-sm">
                                <FileText className="w-4 h-4 text-gray-500" />
                                <span>{order.customer_document_type || "CPF"}: <strong>{order.customer_document}</strong></span>
                              </div>
                            )}
                            
                            {/* Observações de entrega */}
                            {order.delivery_observations && (
                              <div className="text-sm bg-orange-50 p-2 rounded">
                                <span className="font-medium text-orange-700">Obs. Entrega:</span>
                                <p className="text-orange-600 mt-1">{order.delivery_observations}</p>
                              </div>
                            )}
                            
                            {/* Observações dos itens */}
                            {order.item_observations && order.item_observations.length > 0 && (
                              <div className="text-sm bg-purple-50 p-2 rounded">
                                <span className="font-medium text-purple-700">Obs. Itens:</span>
                                {order.item_observations.map((obs, idx) => (
                                  <p key={idx} className="text-purple-600 mt-1">• {obs.name}: {obs.observation}</p>
                                ))}
                              </div>
                            )}
                            
                            {/* Descontos com origem */}
                            {order.benefits && order.benefits.length > 0 && (
                              <div className="text-sm bg-emerald-50 p-2 rounded">
                                <span className="font-medium text-emerald-700">Descontos:</span>
                                {order.benefits.map((benefit, idx) => (
                                  <div key={idx} className="text-emerald-600 mt-1">
                                    • R$ {benefit.value?.toFixed(2)} - {benefit.target}
                                    {benefit.sponsorshipValues && benefit.sponsorshipValues.map((s, sidx) => (
                                      <span key={sidx} className="text-xs ml-2">({s.name}: R$ {s.value?.toFixed(2)})</span>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2 ml-4">
                          {order.ifood_status === 'PLACED' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleConfirmOrder(order.ifood_order_id)}
                                className="bg-blue-500 hover:bg-blue-600 text-white"
                              >
                                Confirmar
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => fetchCancellationReasons(order)}
                                variant="outline"
                                className="border-red-500 text-red-600 hover:bg-red-50"
                              >
                                Cancelar
                              </Button>
                            </>
                          )}
                          {order.ifood_status === 'CONFIRMED' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleStartPreparation(order.ifood_order_id)}
                                className="bg-purple-500 hover:bg-purple-600 text-white"
                              >
                                Iniciar Preparo
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => fetchCancellationReasons(order)}
                                variant="outline"
                                className="border-red-500 text-red-600 hover:bg-red-50"
                              >
                                Cancelar
                              </Button>
                            </>
                          )}
                          {order.ifood_status === "PREPARATION_STARTED" && (
                            <Button
                              size="sm"
                              onClick={() => handleMarkReady(order.ifood_order_id, order.order_type)}
                              className="bg-green-500 hover:bg-green-600 text-white"
                            >
                              Pronto
                            </Button>
                          )}
                          {order.ifood_status === 'READY_TO_PICKUP' && order.order_type === 'DELIVERY' && (
                            <Button
                              size="sm"
                              onClick={() => handleDispatch(order.ifood_order_id)}
                              className="bg-indigo-500 hover:bg-indigo-600 text-white"
                            >
                              Despachar
                            </Button>
                          )}
                          {order.ifood_status === 'READY_TO_PICKUP' && order.order_type === 'TAKEOUT' && (
                            <Button
                              size="sm"
                              onClick={() => handleMarkDelivered(order.ifood_order_id)}
                              className="bg-emerald-500 hover:bg-emerald-600 text-white"
                            >
                              Finalizar
                            </Button>
                          )}
                          {order.ifood_status === 'DISPATCHED' && (
                            <Button
                              size="sm"
                              onClick={() => handleMarkDelivered(order.ifood_order_id)}
                              className="bg-emerald-500 hover:bg-emerald-600 text-white"
                            >
                              Marcar Entregue
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="mt-2 text-xs text-gray-400">
                        Recebido: {order.ifood_created_at ? new Date(order.ifood_created_at).toLocaleString('pt-BR') : new Date(order.created_at).toLocaleString('pt-BR')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Histórico de Eventos</h3>
                <Button
                  onClick={loadEvents}
                  variant="outline"
                  size="sm"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Atualizar
                </Button>
              </div>

              {events.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Nenhum evento registrado</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Tipo</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">ID do Pedido</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Processado</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Origem</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Recebido</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {events.map(event => (
                        <tr key={event.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              event.event_type.includes('PLACED') ? 'bg-yellow-100 text-yellow-800' :
                              event.event_type.includes('CONFIRM') ? 'bg-blue-100 text-blue-800' :
                              event.event_type.includes('CANCEL') ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {event.event_type}
                            </span>
                          </td>
                          <td className="px-4 py-2 font-mono text-sm">
                            {event.correlation_id?.slice(0, 8) || '-'}
                          </td>
                          <td className="px-4 py-2">
                            {event.processed ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            ) : (
                              <Clock className="w-4 h-4 text-yellow-500" />
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {event.source}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {new Date(event.received_at).toLocaleString('pt-BR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* API Tests Tab */}
          {activeTab === 'api-tests' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Terminal className="w-6 h-6 text-red-500" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Testes de API iFood</h3>
                  <p className="text-sm text-gray-500">Teste a conexão direta com a API do iFood usando as credenciais do ambiente</p>
                </div>
              </div>

              {/* Test 1: Authentication */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Key className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">1. Teste de Autenticação</h4>
                      <p className="text-sm text-gray-500">Verifica se as credenciais podem obter um token válido</p>
                    </div>
                  </div>
                  <Button
                    onClick={handleTestAuth}
                    disabled={testingAuth}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    data-testid="btn-test-auth-api"
                  >
                    {testingAuth ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Executar
                  </Button>
                </div>
                
                {authTestResult && (
                  <div className={`mt-4 p-4 rounded-lg ${authTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {authTestResult.success ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <span className={`font-medium ${authTestResult.success ? 'text-green-700' : 'text-red-700'}`}>
                        {authTestResult.success ? 'Autenticação bem-sucedida!' : 'Falha na autenticação'}
                      </span>
                    </div>
                    <pre className="mt-2 p-3 bg-white rounded border text-xs overflow-auto max-h-48">
                      {JSON.stringify(authTestResult, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* Test 2: Event Polling */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Zap className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">2. Teste de Polling de Eventos</h4>
                      <p className="text-sm text-gray-500">Verifica eventos pendentes na fila do iFood (pedidos novos, cancelamentos, etc.)</p>
                    </div>
                  </div>
                  <Button
                    onClick={handleTestPolling}
                    disabled={testingPolling}
                    className="bg-purple-500 hover:bg-purple-600 text-white"
                    data-testid="btn-test-polling-api"
                  >
                    {testingPolling ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Executar
                  </Button>
                </div>
                
                {pollingTestResult && (
                  <div className={`mt-4 p-4 rounded-lg ${pollingTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {pollingTestResult.success ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <span className={`font-medium ${pollingTestResult.success ? 'text-green-700' : 'text-red-700'}`}>
                        {pollingTestResult.success 
                          ? `${pollingTestResult.events_count || 0} eventos encontrados` 
                          : 'Falha no polling'}
                      </span>
                    </div>
                    {pollingTestResult.events && pollingTestResult.events.length > 0 && (
                      <div className="mt-3 mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">Eventos encontrados:</p>
                        <div className="space-y-2 max-h-48 overflow-auto">
                          {pollingTestResult.events.map((evt, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded border text-sm">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                evt.fullCode === 'PLACED' ? 'bg-yellow-100 text-yellow-800' :
                                evt.fullCode === 'CONFIRMED' ? 'bg-blue-100 text-blue-800' :
                                evt.fullCode === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {evt.fullCode || evt.code}
                              </span>
                              <span className="font-mono text-gray-600">{evt.orderId?.slice(0, 8)}...</span>
                              <span className="text-gray-400 text-xs">{new Date(evt.createdAt).toLocaleString('pt-BR')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">Ver resposta completa</summary>
                      <pre className="mt-2 p-3 bg-white rounded border text-xs overflow-auto max-h-64">
                        {JSON.stringify(pollingTestResult, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </div>

              {/* Test 3: Get Order Details */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Search className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">3. Buscar Detalhes de um Pedido</h4>
                      <p className="text-sm text-gray-500">Busca os detalhes completos de um pedido pelo ID</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Input
                    value={orderIdToTest}
                    onChange={(e) => setOrderIdToTest(e.target.value)}
                    placeholder="UUID do pedido iFood (ex: d0a552b3-6fd9-482b-8714-8d53c129a78d)"
                    className="flex-1 font-mono"
                    data-testid="input-order-id-test"
                  />
                  <Button
                    onClick={handleTestOrder}
                    disabled={testingOrder || !orderIdToTest.trim()}
                    className="bg-green-500 hover:bg-green-600 text-white"
                    data-testid="btn-test-order-api"
                  >
                    {testingOrder ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Buscar
                  </Button>
                </div>
                
                {orderTestResult && (
                  <div className={`mt-4 p-4 rounded-lg ${orderTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {orderTestResult.success ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <span className={`font-medium ${orderTestResult.success ? 'text-green-700' : 'text-red-700'}`}>
                        {orderTestResult.success ? 'Pedido encontrado!' : 'Pedido não encontrado ou erro'}
                      </span>
                    </div>
                    {orderTestResult.order && (
                      <div className="mt-3 p-3 bg-white rounded border">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-gray-500">Display ID:</span> <strong>{orderTestResult.order.displayId}</strong></div>
                          <div><span className="text-gray-500">Tipo:</span> <strong>{orderTestResult.order.orderType}</strong></div>
                          <div><span className="text-gray-500">Cliente:</span> <strong>{orderTestResult.order.customer?.name}</strong></div>
                          <div><span className="text-gray-500">Total:</span> <strong>R$ {orderTestResult.order.total?.orderAmount?.toFixed(2)}</strong></div>
                        </div>
                      </div>
                    )}
                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">Ver resposta completa</summary>
                      <pre className="mt-2 p-3 bg-white rounded border text-xs overflow-auto max-h-64">
                        {JSON.stringify(orderTestResult, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Como usar
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>1. Execute o <strong>Teste de Autenticação</strong> para verificar se as credenciais estão funcionando</li>
                  <li>2. Execute o <strong>Teste de Polling</strong> para ver os eventos pendentes na fila do iFood</li>
                  <li>3. Copie um <strong>orderId</strong> de um evento PLACED e use o <strong>Buscar Detalhes</strong> para ver o pedido completo</li>
                  <li>4. Se houver eventos, vá para a aba <strong>Pedidos</strong> e clique em <strong>Buscar Novos Pedidos</strong> para processá-los</li>
                </ul>
              </div>
            </div>
          )}

          {/* Homologation Tab */}
          {activeTab === 'homologation' && (
            <IFoodHomologationTab storeId={storeId} config={config} />
          )}

          {activeTab === "catalog-v2" && (            <IFoodCatalogV2Tab />          )}
        </div>
      </div>
    </div>

      {/* Modal de Cancelamento com Motivos */}

      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Cancelar Pedido #{orderToCancel?.ifood_display_id}</h3>
            <p className="text-sm text-gray-600 mb-4">Selecione o motivo do cancelamento:</p>
            
            {loadingReasons ? (
              <div className="flex justify-center py-4">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {!canCancelOrder ? (
                  // CENÁRIO 2/3: Pedido NÃO pode ser cancelado ou erro
                  <div className="text-center py-6 px-4">
                    <div className="w-12 h-12 rounded-full bg-red-100 mx-auto mb-3 flex items-center justify-center">
                      <XCircle className="w-6 h-6 text-red-500" />
                    </div>
                    <p className="text-sm text-red-600 font-medium">
                      {cancellationMessage}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      O pedido pode já ter sido despachado ou estar em um estágio que não permite cancelamento pelo restaurante.
                    </p>
                  </div>
                ) : cancelReasons.length > 0 ? (
                  // CENÁRIO 1: Pedido pode ser cancelado - lista de motivos
                  cancelReasons.map((reason) => (
                    <label
                      key={reason.cancelCodeId}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                        selectedCancelReason?.cancelCodeId === reason.cancelCodeId ? "border-red-500 bg-red-50" : "border-gray-200"
                      }`}
                    >
                      <input
                        type="radio"
                        name="cancelReason"
                        checked={selectedCancelReason?.cancelCodeId === reason.cancelCodeId}
                        onChange={() => setSelectedCancelReason(reason)}
                        className="mr-3"
                      />
                      <span className="text-sm">{reason.description || reason.cancelCodeId}</span>
                    </label>
                  ))
                ) : (
                  // Fallback caso nenhum motivo seja retornado (não deveria acontecer)
                  <p className="text-sm text-gray-500 text-center py-4">
                    Carregando motivos...
                  </p>
                )}
              </div>
            )}
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedCancelReason(null);
                  setOrderToCancel(null);
                }}
              >
                Voltar
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleCancelOrder}
                disabled={!selectedCancelReason || loadingReasons || !canCancelOrder}
              >
                Confirmar Cancelamento
              </Button>
            </div>
          </div>
        </div>
      )}

    </MainLayout>
  );
};

export default IFoodIntegrationPage;
